import { AnimatePresence, motion } from "framer-motion";
import { FC, useState } from "react";
import styled from "styled-components";

import { Modal } from "@/components/Modal";
import Text from "@/components/Text";
import { CloseButton } from "@/components/AlertDialogs/Dialog";
import DistributionScheduleDetailsTable from "./DistributionScheduleDetailsTable";
import { ReleaseResource } from "@/types/resources";
import DistributionScheduleTipsTable from "./DistributionScheduleTipsTable";
import Button from "@/components/Button";
import DistributionScheduleTipsModal from "./DistributionScheduleTipsModal";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/utils/api";
import ContentSpinner from "@/components/ContentSpinner";
import useAlerts from "@/hooks/useAlerts";

const Dialog = styled(motion.div)`
  width: 90%;
  max-width: 1056px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  text-align: center;
  position: relative;
`;

const DialogHeader = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  padding: 22px 24px;
  text-align: left;
`;

const DialogFooter = styled.div`
  padding: 24px 24px 32px;
  text-align: right;
  button {
    margin-left: 16px;
  }
`;

const StyledButton = styled(Button)`
  font-size: 14px;
  height: 38px;
  line-height: 24px;
  padding: 0 32px;
`;

const StyledContentSpinner = styled(ContentSpinner)`
  margin: 100px 0;
`;

interface Props {
  releaseId: number;
  onClose: () => void;
}

const DistributionScheduleDetailsModal: FC<Props> = ({
  releaseId,
  onClose,
}) => {
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);

  const alerts = useAlerts();

  const { data: release } = useQuery(
    [
      "releases",
      {
        id: releaseId,
      },
    ],
    () => api.get<never, ReleaseResource>(`/v1/releases/${releaseId}`),
    {
      onError: alerts.error,
    }
  );

  return (
    <>
      <Modal>
        <Dialog
          initial={{ scale: 0.3 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.3 }}
        >
          <DialogHeader>
            <Text $type="button">Details</Text>
            <CloseButton onClick={onClose}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13 1L1 13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13 13L1 1"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </CloseButton>
          </DialogHeader>
          {!release ? (
            <StyledContentSpinner />
          ) : (
            <>
              <DistributionScheduleDetailsTable release={release!} />
              <DistributionScheduleTipsTable
                tips={release.tips ?? []}
                releaseId={release.id}
              />
              <DialogFooter>
                <StyledButton color="secondary" onClick={onClose}>
                  Close
                </StyledButton>
                <Button
                  color="colorized"
                  onClick={() => setShowAttachmentModal(true)}
                >
                  Edit
                </Button>
              </DialogFooter>
            </>
          )}
        </Dialog>
      </Modal>
      <AnimatePresence>
        {!!showAttachmentModal && (
          <DistributionScheduleTipsModal
            onClose={() => setShowAttachmentModal(false)}
            release={release!}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default DistributionScheduleDetailsModal;
